import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { ChartRendererSupabase } from './ChartRendererSupabase';
import { getColorsForCategories } from './colors';

type HeatingFuelRow = {
  house_heating_fuel: string | null;
  year: number | null;
  // Current cleaned CSV uses `percent_hh`.
  percent_hh?: number | string | null;
  // Older API-backed charts used `percent_of_households` (fallback only).
  percent_of_households?: number | string | null;
};

type ChartBundle = {
  chartData: any;
  chartOptions: any;
  indicator1: string;
  indicator2: string;
  indicatorLabel1: string;
  indicatorLabel2: string;
  smartNarrative: string;
};

const ORDERED_FUELS = [
  'Utility Gas',
  'Electricity',
  'Bottled, tank, or LP gas',
  'Fuel oil, kerosene, etc.',
  'Wood',
  'Solar energy',
  'Coal or coke',
  'Other fuel',
  'No fuel used',
];

const FUEL_NAME_LOOKUP = new Map(
  ORDERED_FUELS.map((f) => [f.toLowerCase(), f])
);

function normalizeFuelName(raw: string): string {
  return FUEL_NAME_LOOKUP.get(raw.toLowerCase()) ?? raw;
}

export interface HeatingFuelMetrics {
  gasPercent: number;
  elecPercent: number;
  latestYear: number;
  smartNarrative: string;
}

interface HouseHeatingFuelProps {
  locationName: string;
  onMetricsLoaded?: (metrics: HeatingFuelMetrics) => void;
}

export default function HouseHeatingFuelSupabase({ locationName, onMetricsLoaded }: HouseHeatingFuelProps) {
  const [bundle, setBundle] = useState<ChartBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const onMetricsLoadedRef = useRef(onMetricsLoaded);
  onMetricsLoadedRef.current = onMetricsLoaded;

  const title = useMemo(() => `Primary Heating Fuel Use - ${locationName}`, [locationName]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        // PostgREST will error if you select a column that doesn't exist, so we try
        // the current schema first and fall back to the legacy column name.
        let data: any[] | null = null;
        let sbError: any = null;

        {
          const res = await supabaseClient
            .from('house_heating_fuel')
            .select('house_heating_fuel, year, percent_hh')
            .ilike('location_name', locationName)
            .order('year', { ascending: true });
          data = res.data as any[] | null;
          sbError = res.error;
        }

        if (sbError && String(sbError.message || '').includes('percent_hh')) {
          const resLegacy = await supabaseClient
            .from('house_heating_fuel')
            .select('house_heating_fuel, year, percent_of_households')
            .ilike('location_name', locationName)
            .order('year', { ascending: true });
          data = resLegacy.data as any[] | null;
          sbError = resLegacy.error;
        }

        if (sbError) throw sbError;

        const rows = (data || []) as HeatingFuelRow[];
        const cleaned = rows
          .filter((r) => {
            const pct = r.percent_hh ?? r.percent_of_households;
            return r.year != null && r.house_heating_fuel && pct != null;
          })
          .map((r) => ({
            year: Number(r.year),
            fuel: normalizeFuelName(String(r.house_heating_fuel)),
            pct: Number(r.percent_hh ?? r.percent_of_households),
          }))
          .filter((r) => Number.isFinite(r.year) && Number.isFinite(r.pct));

        const years = Array.from(new Set(cleaned.map((r) => r.year))).sort((a, b) => a - b);
        const fuelMap = new Map<string, Map<number, number>>();
        cleaned.forEach((r) => {
          if (!fuelMap.has(r.fuel)) fuelMap.set(r.fuel, new Map());
          fuelMap.get(r.fuel)!.set(r.year, r.pct);
        });

        const fuels = ORDERED_FUELS.filter((f) => fuelMap.has(f));
        // If dataset includes fuels outside our list, append them at end.
        for (const f of Array.from(fuelMap.keys())) {
          if (!fuels.includes(f)) fuels.push(f);
        }

        const palette = getColorsForCategories(fuels);
        const datasets = fuels.map((fuel, idx) => ({
          label: fuel,
          data: years.map((y) => fuelMap.get(fuel)?.get(y) ?? null),
          borderColor: palette[idx],
          backgroundColor: palette[idx],
          tension: 0.3,
          borderWidth: 2,
          fill: false,
        }));

        const latestYear = years[years.length - 1];
        const latestRows = cleaned.filter((r) => r.year === latestYear);
        const gas = latestRows.find((r) => r.fuel === 'Utility Gas')?.pct ?? 0;
        const elec = latestRows.find((r) => r.fuel === 'Electricity')?.pct ?? 0;

        const smartNarrative = `In ${locationName}, ${gas.toFixed(1)}% of households use natural gas as their primary heating fuel, while ${elec.toFixed(1)}% use electricity (latest year: ${latestYear}). This distribution provides a simple indicator of building electrification progress.`;

        const next: ChartBundle = {
          chartData: { labels: years, datasets },
          chartOptions: {
            type: 'line',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: 'bottom' },
              title: { display: true, text: 'Primary Heating Fuel Use' },
              tooltip: {
                callbacks: {
                  label: (ctx: any) =>
                    `${ctx.dataset.label}: ${typeof ctx.parsed?.y === 'number' ? ctx.parsed.y.toFixed(1) : 'N/A'}%`,
                },
              },
            },
            scales: {
              y: { beginAtZero: true, title: { display: true, text: '% of Households' } },
              x: { title: { display: true, text: 'Year' } },
            },
          },
          indicator1: `${gas.toFixed(1)}%`,
          indicator2: `${elec.toFixed(1)}%`,
          indicatorLabel1: 'Households using utility gas (latest year)',
          indicatorLabel2: 'Households using electricity (latest year)',
          smartNarrative,
        };

        if (!cancelled) {
          setBundle(next);
          onMetricsLoadedRef.current?.({ gasPercent: gas, elecPercent: elec, latestYear, smartNarrative });
        }
      } catch (e: any) {
        if (!cancelled) {
          setBundle(null);
          setError(e?.message || 'Failed to load heating fuel data.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [locationName]);

  if (loading) {
    return (
      <div className="placeholder-chart">
        <p>Loading heating fuel data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="placeholder-chart">
        <p>Couldn’t load heating fuel data.</p>
        <small style={{ opacity: 0.8 }}>{error}</small>
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="placeholder-chart">
        <p>No heating fuel data found for {locationName}.</p>
      </div>
    );
  }

  return (
    <div>
      <ChartRendererSupabase
        chartData={bundle.chartData}
        chartOptions={bundle.chartOptions}
        height="420px"
        title={title}
        location={locationName}
      />
      <div style={{ marginTop: 12 }}>
        <small style={{ opacity: 0.85 }}>{bundle.smartNarrative}</small>
      </div>
    </div>
  );
}

